<template v-if="summary">
    <div class="row mb-3">
        <div class="col-12">
            <div class="scrollable-buttons">
                <button v-for="period in periods" :key="period.value" class="btn me-2"
                    :class="{ 'btn-primary': selectedPeriod === period.value, 'btn-light': selectedPeriod !== period.value }"
                    @click="updateSummary(period.value)">
                    {{ period.label }}
                </button>
            </div>
        </div>
    </div>
    <div class="row equal-height">
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">Orders by Status</div>
                <div class="card-body">
                    <canvas ref="ordersByStatusChart"></canvas>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">Daily Revenue</div>
                <div class="card-body">
                    <canvas ref="dailyRevenueChart"></canvas>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">Order Types</div>
                <div class="card-body">
                    <canvas ref="orderTypesChart"></canvas>
                </div>
            </div>
        </div>
    </div>
    <div class="row equal-height">
        <div class="col-md-4" v-if="summary?.topItems?.length">
            <div class="card">
                <div class="card-header">Top Selling Items</div>
                <div class="card-body">
                    <ul class="list-group">
                        <li class="list-group-item d-flex justify-content-between align-items-center"
                            v-for="(item, index) in summary?.topItems || []" :key="index">
                            <span>{{ item.item_name }}</span>
                            <span class="badge badge-primary badge-pill">{{ item.total_quantity }} sold</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="info-card">
                <div class="info-header">Average Order Value</div>
                <div class="info-body">
                    <div class="info-value">{{ formatCurrency(summary?.averageOrderValue[0]?.avg_order_value || 0) }}
                    </div>
                    <div class="info-subtitle">Since Last Month</div>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="info-card">
                <div class="info-header">Unique Customers</div>
                <div class="info-body">
                    <div class="info-value">{{ summary?.customerCount[0]?.unique_customers || 0 }}</div>
                    <div class="info-subtitle">Total Customers</div>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="card">
                <div class="card-header">Revenue by Payment Type</div>
                <div class="card-body">
                    <canvas ref="revenueByPaymentTypeChart"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { markRaw } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { formatCurrency } from '@/utils/helpers';

Chart.register(...registerables);

export default markRaw({
    name: 'DashboardCharts',
    data() {
        return {
            selectedPeriod: 'today',
            periods: [
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Last Week', value: 'lastWeek' },
                { label: 'Month to Date', value: 'monthToDate' },
                { label: 'Last Month', value: 'lastMonth' },
                { label: 'All Time', value: 'allTime' }
            ],
            charts: {}
        };
    },
    computed: {
        ...mapGetters('admin', ['summary'])
    },
    mounted() {
        this.updateSummary('today');
    },
    methods: {
        ...mapActions('admin', ['fetchSummary']),
        formatCurrency,
        updateSummary(period) {
            this.selectedPeriod = period;
            this.fetchSummary({ period }).then(() => {
                this.createCharts();
            });
        },
        createCharts() {
            this.createChart('ordersByStatusChart', 'pie', this.summary.ordersByStatus.map(o => o.status),
                this.summary.ordersByStatus.map(o => o.count), 'Orders by Status');
            this.createChart('dailyRevenueChart', 'line', this.summary.dailyRevenue.map(d => d.day.substring(0, 10)),
                this.summary.dailyRevenue.map(d => parseFloat(d.total_revenue / 100, 2)), 'Daily Revenue');
            this.createChart('orderTypesChart', 'bar', this.summary.orderTypes.map(o => o.order_type),
                this.summary.orderTypes.map(o => o.count), 'Order Types');
            this.createChart('revenueByPaymentTypeChart', 'doughnut', this.summary.revenueByPaymentType.map(p =>
                p.payment_type), this.summary.revenueByPaymentType.map(p => parseFloat(p.revenue / 100, 2)), 'Revenue by Payment Type');
        },
        createChart(chartRefName, type, labels, data, title) {
            const chartRef = this.$refs[chartRefName];

            // Destroy existing chart instance if it exists
            if (this.charts[chartRefName]) {
                this.charts[chartRefName].destroy();
            }

            const colors = {
                backgroundColor: [
                    '#007bff',
                    '#6c757d',
                    '#28a745',
                    '#17a2b8',
                    '#dc3545',
                    '#ffc107'
                ],
                borderColor: [
                    '#0056b3',
                    '#545b62',
                    '#1e7e34',
                    '#117a8b',
                    '#bd2130',
                    '#e0a800'
                ]
            };

            const chart = new Chart(chartRef, {
                type: type,
                data: {
                    labels: labels,
                    datasets: [{
                        label: title,
                        data: data,
                        backgroundColor: colors.backgroundColor,
                        borderColor: colors.borderColor,
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    },
                    responsive: true,
                    maintainAspectRatio: false
                }
            });

            // Store the chart instance for later destruction
            this.charts[chartRefName] = markRaw(chart);
        }
    }
});
</script>

<style scoped>
.card {
    margin-top: 1rem;
    border: 1px solid #eaecef;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background: #ffffff;
}

.card:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.card-header {
    background-color: #f9f9f9;
    color: #333;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.75rem 1.25rem;
}

.card-body {
    padding: 1rem;
}

.list-group-item {
    background-color: #f7f7f7;
    border: none;
    color: #444;
    font-weight: normal;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #eaecef;
}
.list-group-item:last-child {
    border-bottom: none;
}

.list-group-item .badge {
    background-color: #007bff;
    color: #fff;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 0.2rem;
}

.info-card {
    background-color: var(--primary-color);
    color: #ffffff;
    border-radius: 10px;
    margin-top: 1rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    text-align: center;
}

.info-header {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
}

.info-body {
    padding: 1rem 0;
}

.info-value {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 0.25rem;
}

.info-subtitle {
    font-size: 1rem;
    font-weight: normal;
    color: #dddddd;
}

.badge {
    background-color: #007bff;
    color: #fff;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 0.2rem;
}

canvas {
    max-width: 100%;
}

.equal-height {
    display: flex;
    flex-wrap: wrap;
}

.equal-height>[class^='col-'] {
    display: flex;
    flex-direction: column;
}

.equal-height .card,
.equal-height .info-card {
    flex: 1;
}

.scrollable-buttons {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 10px;
}

.scrollable-buttons .btn {
    flex: 0 0 auto;
    border-radius: 20px;
    transition: all 0.3s;
    color: #333;
    background-color: #f0f0f0;
}

.scrollable-buttons .btn:hover {
    background-color: #e0e0e0;
}

.scrollable-buttons .btn-primary {
    background-color: #007bff;
    color: #fff;
}
</style>

